import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useDismiss from '../../hooks/useDismiss';
import { useTheme } from '../../config/theme';
import './theme.css';

export const THEME_OPTIONS = [
    { id: 'light', label: 'Light' },
    { id: 'dark', label: 'Dark' },
    { id: 'system', label: 'System' },
];

const labelOf = (choice) => THEME_OPTIONS.find((o) => o.id === choice)?.label || 'System';

const SunIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
    </svg>
);
// The prototype's theme button glyph.
const MoonIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
    </svg>
);
const SystemIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
    </svg>
);
const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
);
const ICONS = { light: SunIcon, dark: MoonIcon, system: SystemIcon };

/**
 * The three theme choices as menuitemradio buttons with a check mark. Used
 * by the top bar's theme menu and, on narrow screens, by the user menu.
 */
export function ThemeMenuItems({ onPicked, itemRefs, onItemKeyDown, asGroup = true }) {
    const { choice, setChoice } = useTheme();
    const items = THEME_OPTIONS.map((o, i) => {
        const Icon = ICONS[o.id];
        const checked = choice === o.id;
        return (
            <button
                key={o.id}
                ref={itemRefs ? (el) => { itemRefs.current[i] = el; } : undefined}
                type="button"
                role="menuitemradio"
                aria-checked={checked}
                className="v2-theme-item"
                tabIndex={itemRefs ? -1 : undefined}
                onKeyDown={onItemKeyDown ? (e) => onItemKeyDown(e, i) : undefined}
                onClick={() => {
                    setChoice(o.id);
                    onPicked?.(o.id);
                }}
            >
                <Icon />
                <span className="v2-theme-item-l">{o.label}</span>
                <span className="v2-theme-check">{checked && <CheckIcon />}</span>
            </button>
        );
    });
    if (!asGroup) return items;
    return (
        <div role="group" aria-label="Theme" className="v2-theme-group">
            {items}
        </div>
    );
}

ThemeMenuItems.propTypes = {
    onPicked: PropTypes.func,
    itemRefs: PropTypes.shape({ current: PropTypes.array }),
    onItemKeyDown: PropTypes.func,
    asGroup: PropTypes.bool,
};

/**
 * The top bar's theme button (prototype `#theme`), as a small menu:
 * Light / Dark / System with a check mark on the current one. Keyboard:
 * Enter/Space/ArrowDown opens and focuses the checked item, arrows and
 * Home/End move, Escape or Tab closes.
 */
export default function ThemeToggle() {
    const { choice, resolved } = useTheme();
    const menuId = `${useId().replace(/:/g, '')}-theme-menu`;
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const buttonRef = useRef(null);
    const itemRefs = useRef([]);
    const close = useCallback(() => setOpen(false), []);
    useDismiss(wrapRef, close, open);

    useEffect(() => {
        if (!open) return;
        const current = THEME_OPTIONS.findIndex((o) => o.id === choice);
        itemRefs.current[Math.max(0, current)]?.focus();
        // Focus only when the menu opens, not when the choice changes in it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const closeAndReturn = () => {
        setOpen(false);
        buttonRef.current?.focus();
    };

    const onItemKeyDown = (e, i) => {
        const n = THEME_OPTIONS.length;
        const focus = (j) => { e.preventDefault(); itemRefs.current[(j + n) % n]?.focus(); };
        if (e.key === 'ArrowDown') focus(i + 1);
        else if (e.key === 'ArrowUp') focus(i - 1);
        else if (e.key === 'Home') focus(0);
        else if (e.key === 'End') focus(n - 1);
        else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closeAndReturn();
        } else if (e.key === 'Tab') setOpen(false);
    };

    const onButtonKeyDown = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
        }
    };

    const Icon = ICONS[choice] || SystemIcon;
    const label = labelOf(choice);
    const title = choice === 'system'
        ? `Theme: light, dark or follow the system (now ${resolved})`
        : 'Theme: light, dark or follow the system';

    return (
        <div className="v2-theme" ref={wrapRef}>
            <button
                ref={buttonRef}
                type="button"
                className="v2-tb-btn v2-theme-btn"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={`Theme: ${label}`}
                title={title}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={onButtonKeyDown}
            >
                <Icon />
                <span className="v2-theme-btn-l" aria-hidden="true">{label}</span>
            </button>
            {open && (
                <div className="v2-menu v2-theme-menu" id={menuId} role="menu" aria-label="Theme">
                    <ThemeMenuItems
                        asGroup={false}
                        itemRefs={itemRefs}
                        onItemKeyDown={onItemKeyDown}
                        onPicked={closeAndReturn}
                    />
                </div>
            )}
        </div>
    );
}
